import { format } from "date-fns";

/**
 * Central Time (San Antonio, Texas) timezone utilities
 * All events in this app are in San Antonio, so we use Central Time consistently
 */

/**
 * Get the CST/CDT offset for a given date (handles DST)
 * CST = UTC-6, CDT = UTC-5
 */
const getCentralTimeOffset = (date: Date): number => {
  // DST in US: Second Sunday of March to First Sunday of November
  const year = date.getFullYear();
  
  // Get second Sunday of March
  const marchFirst = new Date(year, 2, 1);
  const dstStart = new Date(year, 2, 8 + (7 - marchFirst.getDay()) % 7);
  dstStart.setHours(2, 0, 0, 0);
  
  // Get first Sunday of November
  const novFirst = new Date(year, 10, 1);
  const dstEnd = new Date(year, 10, 1 + (7 - novFirst.getDay()) % 7);
  dstEnd.setHours(2, 0, 0, 0);
  
  // Check if date is in DST
  const isDST = date >= dstStart && date < dstEnd;
  
  return isDST ? -5 : -6; // CDT is -5, CST is -6
};

/**
 * Format a UTC/ISO date string for display in a datetime-local input (Central Time)
 * @param isoDate - ISO date string from database (UTC)
 * @returns string formatted for datetime-local input (YYYY-MM-DDTHH:MM)
 */
export const formatForCentralTime = (isoDate: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  
  const offset = getCentralTimeOffset(date);
  
  // Convert UTC to Central Time
  const utcTime = date.getTime();
  const centralTime = new Date(utcTime + offset * 60 * 60 * 1000);
  
  // Format as YYYY-MM-DDTHH:MM
  const year = centralTime.getUTCFullYear();
  const month = String(centralTime.getUTCMonth() + 1).padStart(2, '0');
  const day = String(centralTime.getUTCDate()).padStart(2, '0');
  const hours = String(centralTime.getUTCHours()).padStart(2, '0');
  const minutes = String(centralTime.getUTCMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parse a datetime-local input value as Central Time and return UTC ISO string
 * @param localDateStr - Value from datetime-local input (YYYY-MM-DDTHH:MM)
 * @returns ISO string in UTC
 */
export const parseCentralTimeToUTC = (localDateStr: string): string => {
  if (!localDateStr) return '';
  
  // Parse the local date string
  const [datePart, timePart] = localDateStr.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = (timePart || '12:00').split(':').map(Number);
  
  // Create a date in UTC that represents the Central Time input
  // We need to add the offset to convert Central → UTC
  const tempDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
  const offset = getCentralTimeOffset(tempDate);
  
  // Add the offset (negative offset means we add hours to get UTC)
  const utcTime = tempDate.getTime() - offset * 60 * 60 * 1000;
  
  return new Date(utcTime).toISOString();
};

/**
 * Get date and time strings in Central Time for separate form inputs
 * @param isoDate - ISO date string from database (UTC)
 * @returns Object with date (YYYY-MM-DD) and time (HH:MM) strings
 */
export const getDateTimePartsInCentralTime = (isoDate: string): { date: string; time: string } => {
  const formatted = formatForCentralTime(isoDate);
  if (!formatted) {
    return { date: '', time: '' };
  }
  
  return {
    date: formatted.slice(0, 10),
    time: formatted.slice(11, 16),
  };
};

/**
 * Combine separate date and time strings (in Central Time) to UTC ISO string
 * @param dateStr - Date string (YYYY-MM-DD)
 * @param timeStr - Time string (HH:MM), defaults to 12:00
 * @returns ISO string in UTC
 */
export const combineDateTimeToCentralUTC = (dateStr: string, timeStr?: string): string => {
  if (!dateStr) return '';
  
  const localDateStr = `${dateStr}T${timeStr || '12:00'}`;
  return parseCentralTimeToUTC(localDateStr);
};

/**
 * Get a Date object adjusted to Central Time for display calculations
 * @param isoDate - ISO date string from database (UTC)
 * @returns Date object in Central Time (uses UTC methods for correct values)
 */
export const getCentralTimeDate = (isoDate: string): Date => {
  const date = new Date(isoDate);
  const offset = getCentralTimeOffset(date);
  return new Date(date.getTime() + offset * 60 * 60 * 1000);
};

/**
 * Format a UTC date for user-facing display in Central Time
 * @param isoDate - ISO date string from database (UTC)
 * @param formatString - date-fns format string
 * @returns Formatted string in Central Time
 */
export const formatInCentralTime = (isoDate: string, formatString: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  
  const centralDate = getCentralTimeDate(isoDate);
  
  // Create a date that will display correctly when formatted
  // We use the UTC values from centralDate as if they were local values
  const displayDate = new Date(
    centralDate.getUTCFullYear(),
    centralDate.getUTCMonth(),
    centralDate.getUTCDate(),
    centralDate.getUTCHours(),
    centralDate.getUTCMinutes(),
    centralDate.getUTCSeconds()
  );
  
  return format(displayDate, formatString);
};
